import { getSingleCidrError } from './cidrValidation'
import type { IpAddressAllocation } from '../types/cluster'

export type IpAllocationRowInput = {
	id: string
	label: string
	cidrAzA: string
	cidrAzB: string
	cidrAzC?: string
}

/** Preset EKS IP rows (fixed ids). */
export const IP_ALLOC_PRESET_IDS = ['1', '2', '3'] as const

export const IP_ALLOC_LABEL_SHARED = 'SHARED-WORKERNODE'
export const IP_ALLOC_LABEL_WORKNODE = 'WORKERNODE'
export const IP_ALLOC_LABEL_PODS = 'PODS'

/** Production Pods overlays (three AZs). */
export const IP_ALLOC_PODS_PROD_CIDR_A = '100.64.0.0/18'
export const IP_ALLOC_PODS_PROD_CIDR_B = '100.64.64.0/18'
export const IP_ALLOC_PODS_PROD_CIDR_C = '100.64.128.0/18'

/** Non-production Pods overlays (two AZs). */
export const IP_ALLOC_PODS_NONPROD_CIDR_A = '100.64.0.0/17'
export const IP_ALLOC_PODS_NONPROD_CIDR_B = '100.64.128.0/17'

export function podsCidrFields(isProduction: boolean): {
	cidrAzA: string
	cidrAzB: string
	cidrAzC?: string
} {
	if (isProduction) {
		return {
			cidrAzA: IP_ALLOC_PODS_PROD_CIDR_A,
			cidrAzB: IP_ALLOC_PODS_PROD_CIDR_B,
			cidrAzC: IP_ALLOC_PODS_PROD_CIDR_C
		}
	}
	return {
		cidrAzA: IP_ALLOC_PODS_NONPROD_CIDR_A,
		cidrAzB: IP_ALLOC_PODS_NONPROD_CIDR_B,
		cidrAzC: undefined
	}
}

/** Uppercase letters and hyphens only. */
export function sanitizeAllocationLabelInput(raw: string): string {
	return raw.toUpperCase().replace(/[^A-Z-]/g, '')
}

export function getAllocationLabelError(label: string): string | undefined {
	const trimmed = label.trim()
	if (!trimmed) return 'Label is required'
	if (!/^[A-Z]+(-[A-Z]+)*$/.test(trimmed)) {
		return "Use uppercase letters and hyphens only (e.g. SHARED-WORKERNODE)"
	}
	return undefined
}

export function migrateLegacyIpLabel(label: string): string {
	const t = label.trim()
	const lower = t.toLowerCase()
	if (
		lower === 'shared nodegroup' ||
		lower === 'shared-workernode' ||
		lower === 'shared-worker'
	) {
		return IP_ALLOC_LABEL_SHARED
	}
	if (
		lower === 'workload nodegroup' ||
		lower === 'worknode' ||
		lower === 'workernode'
	) {
		return IP_ALLOC_LABEL_WORKNODE
	}
	if (lower === 'pods') {
		return IP_ALLOC_LABEL_PODS
	}
	return sanitizeAllocationLabelInput(t)
}

export function isPresetIpRowId(id: string): boolean {
	return id === '1' || id === '2' || id === '3'
}

/** Pods row uses fixed RFC1918-style overlays; CIDRs are not user-editable. */
export function isPodsPresetRow(row: Pick<IpAllocationRowInput, 'id' | 'label'>): boolean {
	return row.id === '3' || row.label.trim().toLowerCase() === 'pods'
}

/** User-added rows use these id prefixes across cluster / account flows. */
export function isCustomAllocationRowId(id: string): boolean {
	return id.startsWith('custom-') || id.startsWith('ip-')
}

export function isInactiveIpRow(row: IpAllocationRowInput, isProduction: boolean): boolean {
	const noCidr =
		!(row.cidrAzA ?? '').trim() &&
		!(row.cidrAzB ?? '').trim() &&
		(!isProduction || !(row.cidrAzC ?? '').trim())
	if (!noCidr) return false
	if (isCustomAllocationRowId(row.id)) {
		return !(row.label ?? '').trim()
	}
	return true
}

function rowComplete(row: IpAllocationRowInput, isProduction: boolean): boolean {
	return (
		Boolean((row.cidrAzA ?? '').trim()) &&
		Boolean((row.cidrAzB ?? '').trim()) &&
		(!isProduction || Boolean((row.cidrAzC ?? '').trim()))
	)
}

function rowCidrsValid(row: IpAllocationRowInput, isProduction: boolean): boolean {
	return (
		!getSingleCidrError(row.cidrAzA) &&
		!getSingleCidrError(row.cidrAzB) &&
		(!isProduction || !getSingleCidrError(row.cidrAzC ?? ''))
	)
}

/**
 * @param ipRequired — `true` for EKS (preset rows must be complete; optional empty custom rows).
 *                    `false` for Non-EKS (rows may be left empty; any row with data must be complete and valid).
 */
export function isIpAllocationsInputValid(
	rows: IpAllocationRowInput[],
	isProduction: boolean,
	ipRequired: boolean
): boolean {
	for (const row of rows) {
		const custom = isCustomAllocationRowId(row.id)
		if (ipRequired && !custom) {
			if (!rowComplete(row, isProduction) || !rowCidrsValid(row, isProduction)) {
				return false
			}
			continue
		}
		if (ipRequired && custom) {
			if (isInactiveIpRow(row, isProduction)) continue
			if (!rowComplete(row, isProduction) || !rowCidrsValid(row, isProduction)) return false
			if (getAllocationLabelError(row.label)) return false
			continue
		}
		// Non-EKS: optional
		if (isInactiveIpRow(row, isProduction)) continue
		if (!rowComplete(row, isProduction)) return false
		if (!rowCidrsValid(row, isProduction)) return false
		if (custom && getAllocationLabelError(row.label)) return false
	}
	return true
}

export function getDefaultIpAllocationRows(isProduction: boolean): IpAllocationRowInput[] {
	return [
		{
			id: '1',
			label: IP_ALLOC_LABEL_SHARED,
			cidrAzA: '',
			cidrAzB: '',
			cidrAzC: isProduction ? '' : undefined
		},
		{
			id: '2',
			label: IP_ALLOC_LABEL_WORKNODE,
			cidrAzA: '',
			cidrAzB: '',
			cidrAzC: isProduction ? '' : undefined
		},
		{
			id: '3',
			label: IP_ALLOC_LABEL_PODS,
			...podsCidrFields(isProduction)
		}
	]
}

function ensureEksPresetRows(rows: IpAllocationRowInput[], isProduction: boolean): IpAllocationRowInput[] {
	const customs = rows.filter((r) => isCustomAllocationRowId(r.id))
	const presetById = new Map<string, IpAllocationRowInput>()
	for (const r of rows) {
		if (isCustomAllocationRowId(r.id)) continue
		const id = String(r.id ?? '').trim()
		if (isPresetIpRowId(id)) {
			presetById.set(id, r)
		}
	}
	const defaults = getDefaultIpAllocationRows(isProduction)
	const merged: IpAllocationRowInput[] = IP_ALLOC_PRESET_IDS.map((pid) => {
		const existing = presetById.get(pid)
		const base = defaults.find((d) => d.id === pid)!
		if (pid === '3') {
			return {
				id: '3',
				label: IP_ALLOC_LABEL_PODS,
				...podsCidrFields(isProduction)
			}
		}
		if (!existing) return base
		return {
			...existing,
			label: migrateLegacyIpLabel(existing.label),
			cidrAzC: isProduction ? String(existing.cidrAzC ?? '') : undefined
		}
	})
	return [...merged, ...customs]
}

/** Normalize allocations from form/API (ids may be missing after store sync). */
export function normalizeIpAllocationsFromForm(
	raw: unknown,
	isProduction: boolean,
	ipRequired = true
): IpAllocationRowInput[] {
	const mapRow = (row: Record<string, unknown>, i: number): IpAllocationRowInput => {
		let id = row.id != null && String(row.id).trim() ? String(row.id) : ''
		if (!id) {
			id = ipRequired && i < 3 ? String(i + 1) : `custom-${i}`
		}
		return {
			id,
			label: migrateLegacyIpLabel(String(row.label ?? '')),
			cidrAzA: String(row.cidrAzA ?? ''),
			cidrAzB: String(row.cidrAzB ?? ''),
			cidrAzC: isProduction ? String(row.cidrAzC ?? '') : undefined
		}
	}

	if (!ipRequired) {
		if (!Array.isArray(raw) || raw.length === 0) return []
		const mapped = (raw as Record<string, unknown>[]).map((row, i) => mapRow(row, i))
		// Drop empty preset rows; keep optional custom rows (including in-progress empty).
		return mapped.filter(
			(row) => !(isInactiveIpRow(row, isProduction) && !isCustomAllocationRowId(row.id))
		)
	}

	if (!Array.isArray(raw) || raw.length === 0) {
		return getDefaultIpAllocationRows(isProduction)
	}
	const mapped = (raw as Record<string, unknown>[]).map((row, i) => mapRow(row, i))
	return ensureEksPresetRows(mapped, isProduction)
}

function applyPodsConstantsToRow(
	row: IpAllocationRowInput,
	isProduction: boolean
): IpAllocationRowInput {
	if (!isPodsPresetRow(row)) return row
	return {
		...row,
		id: '3',
		label: IP_ALLOC_LABEL_PODS,
		...podsCidrFields(isProduction)
	}
}

export function buildIpAllocationsForSubmit(
	rows: IpAllocationRowInput[],
	isProduction: boolean,
	ipRequired: boolean
): IpAddressAllocation[] {
	const out: IpAddressAllocation[] = []
	const normalized = ipRequired ? ensureEksPresetRows(rows, isProduction) : rows
	for (const row of normalized.map((r) => applyPodsConstantsToRow(r, isProduction))) {
		if (isInactiveIpRow(row, isProduction)) continue
		if (isProduction) {
			out.push({
				label: row.label,
				cidrAzA: row.cidrAzA.trim(),
				cidrAzB: row.cidrAzB.trim(),
				cidrAzC: (row.cidrAzC ?? '').trim()
			})
		} else {
			out.push({
				label: row.label,
				cidrAzA: row.cidrAzA.trim(),
				cidrAzB: row.cidrAzB.trim()
			})
		}
	}
	return out
}
